import type { ButtonHTMLAttributes, Ref } from 'react'
import { useButtonSkin, type ButtonVariant, type ButtonSize } from './buttonSkin'
import './primitives.css'

type Variant = ButtonVariant
type Size = ButtonSize

/**
 * Hit targets: 33px page, 30px panel, 24px inline — rising to 44px below 640px.
 *
 * **M0-R29 is met through `useHitTarget`, and could not be met in CSS** (#90).
 * `min-height` is written inline below so a caller cannot shrink it, and an
 * inline declaration beats a media query exactly as it beats any other
 * selector. Measured at an emulated 375px viewport: with
 * `@media (max-width: 640px) { .btn { min-height: 44px } }` present, the box was
 * still 30px; removing the inline value made it 44px. So the fix had to come
 * through the hook. A media query here would have left every stylesheet-reading
 * test green and phones on 30px buttons — and it would also have been a second
 * breakpoint source, which CLAUDE.md forbids.
 *
 * The 24px `inline` target is below the 44px floor **on desktop only**,
 * deliberately — it is for dense desktop rows. Below 640px it lifts with the
 * rest: M0-R29 states no size exemption, and 24px under a finger is the case
 * the rule exists for.
 *
 * The policy itself lives in `platform/responsive/hitTarget.ts`, which is its
 * one definition — `Sidebar` had written its own `44 : 33`, and #90 would
 * otherwise have had to be applied twice without noticing the second.
 */

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant
  size?: Size
  /** Forwarded. See `Field`'s `ref` for why this is declared rather than inherited. */
  ref?: Ref<HTMLButtonElement>
}

/**
 * The skin — fills, boundaries, hover, focus — lives in primitives.css, not in
 * a style object. An inline style beats every selector, so a fill written here
 * could not be overridden by a `:hover` rule at any specificity, and the hover
 * state would be silently inert. That is not theoretical: it is what happened
 * twice, first via the do-nothing `.hoverable` class and then via hover rules
 * that lost to inline fills.
 *
 * `min-height` is the deliberate exception, and inline for the opposite reason:
 * it is the one property a caller must NOT be able to override, so it wants to
 * win the cascade.
 */
export function Button({
  variant = 'chip',
  size = 'panel',
  disabled,
  style,
  type,
  className,
  ...rest
}: Props) {
  // The class list and the floor come from `useButtonSkin`, shared with
  // `ButtonLink`. They used to be built here and rebuilt there, which is the
  // same shape as the four hand-copies #334 was — see buttonSkin.ts.
  const skin = useButtonSkin(variant, size, className, style)
  return (
    <button
      className={skin.className}
      disabled={disabled}
      // HTML defaults to type="submit". A Cancel or Add-filter button rendered
      // inside a form would submit it — a bug nobody writes deliberately, and
      // one that only shows up once forms exist.
      type={type ?? 'button'}
      style={skin.style}
      {...rest}
    />
  )
}
